import type {
  Block, Cell, ChipCell, Column, FormBlock, FormField, FormLine, TableBlock,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Content & Localization › Help centre (`ct-help`) and Troubleshooting trees
 * (`ct-trees`), read from `GET /v1/admin/help/articles` and
 * `GET /v1/admin/help/troubleshooters` (go-wallet#158/#159, FR-21.1/21.3).
 *
 * # A catalogue, read-only — which is not the same as deferring the section
 *
 * These screens are version editors in the design: authoring an article means
 * publishing a new version per language, with a diff and an `effective_from`
 * (law 6). That CONTROL is not designed yet and is not inherited here. But the
 * catalogue behind it — which keys exist, and which languages each is live in —
 * is a plain read, and withholding it because the editor is unbuilt would leave
 * an operator unable to answer "does a guide for this exist" at all.
 *
 * So: the same posture as `pr-lists`. The list reads; the screen says outright
 * that authoring is not wired.
 *
 * # The gap between `languages` and `live_languages` IS the queue
 *
 * "The difference between these two arrays is the translation queue, and it is
 * what a key with nothing live looks like — which is a `410 Gone` on the
 * customer surface." A key with drafts in four languages and none live is not a
 * key in progress; it is a link in a shipped app binary that returns nothing.
 * So a key with no live language is the loud row on this screen.
 *
 * # A key is permanent, and the screen says why
 *
 * "It cannot be renamed and cannot be deleted: deep links to it live in app
 * binaries that went through store review, in notifications already delivered,
 * and in search-engine indexes." That is the reason there is no delete
 * affordance here and never will be — not an omission waiting to be filled.
 */

export const HELP_ARTICLES_PATH = '/help/articles'
export const TROUBLESHOOTERS_PATH = '/help/troubleshooters'

const ARTICLES_SOURCE = 'postgres · help_articles (GET /v1/admin/help/articles)'
const TREES_SOURCE = 'postgres · help_troubleshooters (GET /v1/admin/help/troubleshooters)'
const AUTHORITY = 'operational · what the customer surface serves'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function langs(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v !== '')
}

interface Entry {
  readonly key: string
  readonly createdBy: string
  readonly createdAt: string
  readonly languages: readonly string[]
  readonly liveLanguages: readonly string[]
}

function readEntry(raw: unknown, keyField: string): Entry | null {
  if (!isObject(raw)) return null
  const key = text(raw[keyField])
  if (key === '') return null
  return {
    key,
    createdBy: text(raw['created_by']),
    createdAt: text(raw['created_at']),
    languages: langs(raw['languages']),
    liveLanguages: langs(raw['live_languages']),
  }
}

/**
 * What the customer surface does with this key right now.
 *
 * A key with nothing live is a `410 Gone` to anybody following a deep link out
 * of a shipped app binary or an already-delivered notification. That is the
 * loud state, and it is louder when the key HAS drafts — because then somebody
 * believes it exists.
 */
function liveChip(e: Entry): ChipCell {
  if (e.liveLanguages.length === 0) {
    return [
      e.languages.length === 0 ? 'NOTHING LIVE — no version at all' : 'NOTHING LIVE — drafts only',
      'r',
    ]
  }
  return [`live in ${e.liveLanguages.join(', ')}`, 'g']
}

const COLS: readonly Column[] = [
  ['Key', 'm', 'minmax(220px,1.5fr)'],
  ['Has a version in', 'x', 'minmax(160px,1.2fr)'],
  ['Awaiting translation', 'x', 'minmax(160px,1.2fr)'],
  ['On the customer surface', 'c', 'minmax(200px,1.4fr)'],
  ['Issued', 'm', '120px'],
  ['By', 'x', '150px'],
]

function row(e: Entry): readonly Cell[] {
  const live = new Set(e.liveLanguages)
  // The gap between the two arrays IS the translation queue.
  const pending = e.languages.filter((l) => !live.has(l))
  return [
    e.key,
    e.languages.length === 0 ? '— no version in any language' : e.languages.join(', '),
    pending.length === 0 ? '— none' : pending.join(', '),
    liveChip(e),
    e.createdAt === '' ? '— not returned' : e.createdAt.slice(0, 10),
    e.createdBy === '' ? '— unattributed' : e.createdBy,
  ]
}

function compose(
  entries: readonly Entry[],
  unreadable: number,
  what: string,
  source: string,
  readAt: Date,
): ScreenData {
  const dark = entries.filter((e) => e.liveLanguages.length === 0)

  const table: TableBlock = {
    t: 'table',
    title: `${what} · every issued key`,
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: entries.map(row),
    footer: `${String(entries.length)} key(s)${dark.length === 0 ? '' : ` · ${String(dark.length)} serving nothing`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'A key is PERMANENT: it cannot be renamed and cannot be deleted, because deep links to it live in app binaries that went through store review, in notifications already delivered, and in search-engine indexes. The gap between the languages a key has a version in and the ones it is live in is the translation queue.',
  }

  const blocks: Block[] = [table]

  if (dark.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: '410 Gone',
      title: `${String(dark.length)} key(s) serve nothing to a customer`,
      body: 'A key with no live version returns 410 Gone on the customer surface — to anybody following a deep link out of a shipped app binary, an already-delivered notification, or a search result. A key with drafts and nothing live is the worse case of the two, because somebody believes it exists.',
      ref: dark.map((e) => e.key).join(' · '),
    })
  }

  if (entries.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: what,
      title: 'No key has been issued',
      body: 'Which means the self-service funnel has nothing to offer: every context resolves to "no guides, here is a person". That still reaches a person, and it is the state that makes the support queue the only path.',
      ref: 'the read returned no keys',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} key(s) could not be read`,
      body: 'Counted rather than dropped. A key missing from this catalogue is one nobody knows is dark.',
      ref: 'an entry with no key names nothing and is refused',
    })
  }

  return {
    blocks,
    provenance: { kind: 'live', source, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: entries.map((e) => e.key),
  }
}

/**
 * A catalogue read, and the controls that act on what it lists.
 *
 * `controls` is read beside the catalogue and its failure cannot fail it:
 * `readCaller` resolves every error to "not held", so a `/me` that is down
 * leaves the catalogue readable and every control dead with the reason stated.
 */
function loader(
  what: string, path: string, source: string, listKey: string, keyField: string,
  controls?: (api: ApiClient, caller: Caller) => readonly Block[],
): ScreenLoader {
  return async (api: ApiClient) => {
    let payload: unknown
    const who = controls === undefined ? null : callerBeside(api)
    try {
      payload = await api.get<unknown>(path)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      const reason = error instanceof ApiError
        ? `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
        : 'The backend could not be reached.'
      return {
        blocks: [{
          t: 'note',
          tone: 'r',
          kicker: what,
          title: `${what} could not be read`,
          body: `${reason} Which keys exist, and which of them serve nothing to a customer, are both unknown from this read — and an unread catalogue is not an empty one.`,
          ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : 'Nothing was read.',
        }],
        provenance: { kind: 'unread', source, reason },
      }
    }

    const list = isObject(payload) ? payload[listKey] : undefined
    if (!Array.isArray(list)) {
      const reason = 'The endpoint returned a shape this console does not know.'
      return {
        blocks: [{ t: 'note', tone: 'r', kicker: what, title: `${what} could not be read`, body: reason, ref: 'Nothing was read.' }],
        provenance: { kind: 'unread', source, reason },
      }
    }

    const entries: Entry[] = []
    let unreadable = 0
    for (const raw of list as readonly unknown[]) {
      const parsed = readEntry(raw, keyField)
      if (parsed === null) unreadable += 1
      else entries.push(parsed)
    }
    const screen = compose(entries, unreadable, what, source, new Date())
    if (controls === undefined || who === null) return screen
    return { ...screen, blocks: [...screen.blocks, ...controls(api, await who)] }
  }
}


// ---------------------------------------------------------------------------
// One key's version history, opened from a row
// ---------------------------------------------------------------------------

/**
 * Every version of one content key, in every language.
 *
 * A version history is a READ — the record of what was true — and this one
 * includes "drafts, rejections and superseded versions". That is deliberate:
 * a version number is "NEVER REUSED, including after a rejection", so a gap in
 * the sequence is a rejection somebody should be able to see rather than a
 * missing row.
 *
 * `review_state` and `live` are separate facts, the same way they are on the
 * translation queue: `live` is what a customer is served now, and a version can
 * be published and later closed by a successor without its review state
 * rewinding.
 *
 * Hangs off a row rather than a screen. See `openers.ts`.
 */
const VERSION_COLS: readonly Column[] = [
  ['Language', 'm', '100px'],
  ['Version', 'm', '90px'],
  ['Title', 'x', 'minmax(200px,1.5fr)'],
  ['Review state', 'c', '150px'],
  ['Serving now', 'c', '140px'],
  ['Written by', 'x', 'minmax(150px,1.1fr)'],
]

function versionRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const lang = text(raw['lang'])
  if (lang === '') return null
  const state = text(raw['review_state'])
  const live = typeof raw['live'] === 'boolean' ? raw['live'] : null
  const version = typeof raw['version'] === 'number' ? raw['version'] : null
  return [
    lang,
    version === null ? '—' : `v${String(version)}`,
    text(raw['title']) === '' ? '— no title' : text(raw['title']),
    state === 'in_review'
      ? (['awaiting a human', 'a'] as ChipCell)
      : state === 'published'
        ? (['published', 'g'] as ChipCell)
        : state === 'rejected'
          ? (['rejected — this number is spent', 'r'] as ChipCell)
          : state === 'draft'
            ? (['draft', 'n'] as ChipCell)
            : ([state === '' ? '— not stated' : state, 'r'] as ChipCell),
    live === null
      ? (['— not stated', 'r'] as ChipCell)
      : live
        ? (['serving', 'g'] as ChipCell)
        : (['not serving', 'n'] as ChipCell),
    text(raw['authored_by']) === '' ? '— unattributed' : text(raw['authored_by']),
  ]
}

export function contentVersionsReader(base: string, listKey: string, what: string) {
  return async (api: ApiClient, key: string): Promise<ScreenData> => {
    const wanted = key.trim()
    const source = `postgres · ${what} versions (GET /v1/admin${base}/{key}/versions)`
    if (wanted === '') {
      const reason = 'No key was given, so no history was looked up.'
      return {
        blocks: [{ t: 'note', tone: 'r', kicker: what, title: 'No key was requested', body: reason, ref: 'a row with no key names nothing' }],
        provenance: { kind: 'unread', source, reason },
      }
    }

    let payload: unknown
    try {
      payload = await api.get<unknown>(`${base}/${encodeURIComponent(wanted)}/versions`)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      const reason = error instanceof ApiError
        ? `The history endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
        : 'The backend could not be reached.'
      return {
        blocks: [{
          t: 'note', tone: 'r', kicker: what,
          title: `The history of ${wanted} could not be read`,
          body: `${reason} Which languages this key has ever been written in, and which of them a customer is served, are unknown from this read.`,
          ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : `key asked for: ${wanted}`,
        }],
        provenance: { kind: 'unread', source, reason },
      }
    }

    const list = isObject(payload) ? payload[listKey] : undefined
    const raw = Array.isArray(list) ? (list as readonly unknown[]) : []
    const rows: (readonly Cell[])[] = []
    let unreadable = 0
    for (const entry of raw) {
      const parsed = versionRow(entry)
      if (parsed === null) unreadable += 1
      else rows.push(parsed)
    }

    const blocks: Block[] = [{
      t: 'table',
      title: `${wanted} · every version, in every language`,
      src: 'ledger',
      facets: false,
      cols: VERSION_COLS,
      rows,
      footer: `${String(rows.length)} version(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
      foot2:
        'Drafts, rejections and superseded versions are all here. A version number is never reused — including after a rejection — so a gap in the sequence is a rejection somebody should be able to see rather than a missing row. “Serving now” is a different fact from the review state: a version can be published and later closed by a successor without its review state rewinding.',
    }]

    if (rows.length === 0 && unreadable === 0) {
      blocks.push({
        t: 'note',
        tone: 'a',
        kicker: what,
        title: `${wanted} has never been written in any language`,
        body: 'The key exists and nothing has ever been drafted against it. A customer following a link to it is served a 410 — the key is permanent, so it cannot be tidied away, and the remedy is to write it.',
        ref: `${wanted} · no version in any language`,
      })
    }

    return { blocks, provenance: { kind: 'live', source, authority: AUTHORITY, readAt: readAtStamp(new Date()) } }
  }
}

export const readHelpArticleVersions = contentVersionsReader(HELP_ARTICLES_PATH, 'versions', 'Help article')
export const readTroubleshooterVersions = contentVersionsReader(TROUBLESHOOTERS_PATH, 'versions', 'Troubleshooter')

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Two permissions, and the split between them is the whole workflow.
 *
 * `help.article.write` is **Support** — issuing a key, authoring a draft, and
 * sending it for review. `help.article.publish` is **Ops** — publishing,
 * rejecting and retiring. Troubleshooters carry the same pair; the backend
 * registers no separate permission for them.
 *
 * The console must not merge them. The backend split submit from publish into
 * separate ROUTES for exactly this reason, and says so: a single route
 * dispatching on a body field "puts the permission model inside an `if`", and
 * would have had to carry the wider of the two — letting a support agent
 * publish by sending a different string. A single form here with an action
 * picker spanning both would rebuild that hole in the UI, so the authoring
 * controls and the publishing control are separate blocks with separate
 * refusals.
 *
 * Within Ops the three transitions DO share one control, and that is not the
 * same compromise: publish, reject and retire carry one permission, so an
 * action picker over them widens nothing.
 */
const PERM_CONTENT_WRITE = 'help.article.write'
const PERM_CONTENT_PUBLISH = 'help.article.publish'

/** What the two halves are called where an operator has to ask for them. */
const ROLE_AUTHOR = 'Support'
const ROLE_PUBLISH = 'Ops'

/**
 * The reason codes for authoring, and for deciding what customers read.
 *
 * Separate lists because they answer different questions. An author says why
 * the words are changing; a publisher says why this version is the one a
 * hundred thousand customers get, or why it is not.
 */
const AUTHOR_REASONS: readonly (readonly [string, string, string])[] = [
  ['new_guide', 'A situation had no guide', ''],
  ['content_correction', 'The guide said something wrong', ''],
  ['clarity', 'The guide was accurate and unclear', ''],
  ['product_change', 'The product changed under it', ''],
  ['policy_change', 'A policy or a regulator changed under it', ''],
  ['translation', 'A language that had no version', ''],
]

const PUBLISH_REASONS: readonly (readonly [string, string, string])[] = [
  ['review_passed', 'Reviewed and correct', 'publish'],
  ['urgent_correction', 'Replacing a version that is wrong now', 'publish'],
  ['review_failed', 'Sent back to the author', 'reject'],
  ['factually_wrong', 'States something untrue', 'reject'],
  ['superseded_by_product', 'The situation no longer arises', 'retire'],
  ['legally_withdrawn', 'Withdrawn on legal or regulatory advice', 'retire'],
]

/**
 * A language tag, as the backend normalises it.
 *
 * Free text rather than a picker, because the set is BCP-47 and the backend
 * accepts a language or a language-region tag — a fixed list here would refuse
 * `nl-be` on a deployment that serves it. The note says what shape is accepted
 * so the refusal arrives before the typing rather than after it.
 */
const LANG_FIELD: FormField = {
  name: 'lang',
  label: 'Language',
  kind: 'text',
  required: true,
  mono: true,
  placeholder: 'en',
  note: 'A BCP-47 language or language-region tag — `en`, `nl-be`. Stored lowercase.',
}

/** What a content screen is: the article family, or the troubleshooter family. */
interface ContentFamily {
  /** `/help/articles` or `/help/troubleshooters`. */
  readonly path: string
  /** The body field naming the key on the create call. */
  readonly keyField: string
  /** `article` / `guide`, for the sentences. */
  readonly noun: string
  /**
   * The article the noun takes.
   *
   * Carried rather than derived. "a"/"an" off the first letter is right for
   * `article` and `guide` and wrong for the first noun somebody adds that
   * starts with a silent consonant or a long U — and a heading reading "Issue
   * a article key" is the kind of defect that survives review because nobody
   * reads their own template output as English.
   */
  readonly determiner: string
  /** The audit subject prefix the backend sets. */
  readonly subject: string
  /** The fields that differ between an article version and a guide version. */
  readonly bodyFields: readonly FormField[]
}

export const HELP_ARTICLE_FAMILY: ContentFamily = {
  path: HELP_ARTICLES_PATH,
  keyField: 'article_key',
  noun: 'article',
  determiner: 'an',
  subject: 'help_article',
  bodyFields: [
    { name: 'category', label: 'Category', kind: 'text', required: false },
    { name: 'title', label: 'Title', kind: 'text', required: true },
    { name: 'summary', label: 'Summary', kind: 'textarea', required: false },
    {
      name: 'body_markdown', label: 'Body (markdown)', kind: 'textarea', required: true,
      note: 'What the customer reads.',
    },
  ],
}

export const TROUBLESHOOTER_FAMILY: ContentFamily = {
  path: TROUBLESHOOTERS_PATH,
  keyField: 'troubleshooter_key',
  noun: 'guide',
  determiner: 'a',
  subject: 'help_troubleshooter',
  bodyFields: [
    { name: 'title', label: 'Title', kind: 'text', required: true },
    { name: 'summary', label: 'Summary', kind: 'textarea', required: false },
    {
      name: 'fallback_article_key', label: 'Fallback article key', kind: 'text',
      required: false, mono: true,
      note: 'Where a customer lands when the tree runs out of branches.',
    },
    {
      name: 'tree', label: 'Decision tree (JSON)', kind: 'textarea', required: true,
      mono: true,
      note: 'Sent to the backend exactly as typed. The validator checks the document that gets stored, so this console does not reformat it.',
    },
  ],
}

/**
 * Issuing a key.
 *
 * # A key is permanent, and the control says so before it is created
 *
 * It cannot be renamed and cannot be deleted, because deep links to it live in
 * app binaries that went through store review, in notifications already
 * delivered, and in search indexes. That makes this one of the few creates in
 * the console that is genuinely irreversible, which is why it asks for the key
 * to be typed a second time rather than offering a confirm checkbox: the cost
 * of a typo here is a permanent key nobody can tidy away.
 */
function createKeyForm(f: ContentFamily, api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_CONTENT_WRITE, ROLE_AUTHOR, `Issuing ${f.determiner} ${f.noun} key`,
  )
  return {
    t: 'form',
    title: `Issue ${f.determiner} ${f.noun} key`,
    lead: `A key is permanent: it cannot be renamed and cannot be deleted, because deep links to it end up in shipped app binaries, in notifications already delivered and in search indexes. Issuing one serves nothing on its own — the key is dark until a version is published in some language.`,
    fields: [
      {
        name: 'key', label: 'Key', kind: 'text', required: true, mono: true,
        placeholder: 'payout-late',
        note: 'Lowercase, stable, and forever. Name the situation rather than the current wording.',
      },
    ],
    reasons: AUTHOR_REASONS,
    reasonNote: 'Recorded against the key.',
    auditLine: `admin.help.${f.noun === 'article' ? 'article' : 'troubleshooter'}.create · actor · ${f.subject}:<key> · reason`,
    cta: 'Issue key',
    ctaNote: 'Serves nothing until a version is published.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(f.path, { [f.keyField]: v['key'], reason: v['reason'] }),
        'The key was issued',
        () => [['key', String(v['key']), 'dark until a version is published']],
      ),
    } : {}),
  }
}

/**
 * Authoring one language's version of one key.
 *
 * # This is a draft and nothing else, and the omissions are the backend's
 *
 * There is no version field and no state field. The number is allocated by the
 * store from the highest that exists — a console that could choose one could
 * re-use a number a support conversation already cited — and the state is
 * always `draft`, enforced independently by an insert guard. A form offering
 * either would be offering a choice the server ignores.
 *
 * # Why this is not `versioned`
 *
 * Law 6's control is for an entity whose live row is being superseded on an
 * effective date. This publishes nothing: it adds a draft that a DIFFERENT
 * permission later publishes, and the boundary is the publish rather than a
 * date typed here. Rendering an effective-date field would promise the author
 * control over when their words go live, which is exactly what the Support/Ops
 * split exists to deny them.
 */
function authorVersionForm(f: ContentFamily, api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_CONTENT_WRITE, ROLE_AUTHOR, `Authoring ${f.determiner} ${f.noun} version`,
  )
  return {
    t: 'form',
    title: 'Author a version',
    lead: 'Adds a draft in one language. It reaches no customer until somebody with the publishing permission publishes it, and the version number is allocated by the backend rather than chosen here.',
    fields: [
      {
        name: 'key', label: 'Key', kind: 'text', required: true, mono: true,
        note: `The ${f.noun} this version belongs to. It must already exist.`,
      },
      LANG_FIELD,
      ...f.bodyFields,
    ],
    reasons: AUTHOR_REASONS,
    reasonNote: 'Read by whoever reviews this, who will not have your screen in front of them.',
    auditLine: `admin.help.${f.noun === 'article' ? 'article' : 'troubleshooter'}.draft · actor · ${f.subject}:<key>/<lang> · reason`,
    cta: 'Save draft',
    ctaNote: 'Creates a draft. Publishing is a separate act under a different permission.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(`${f.path}/${String(v['key'])}/versions`, {
          lang: v['lang'],
          ...(f.keyField === 'article_key'
            ? {
                category: v['category'] ?? '',
                title: v['title'],
                summary: v['summary'] ?? '',
                body_markdown: v['body_markdown'],
              }
            : {
                title: v['title'],
                summary: v['summary'] ?? '',
                fallback_article_key: v['fallback_article_key'] ?? '',
                // Passed through as typed. The backend validates the document
                // it stores, and re-encoding here would validate one document
                // and store another.
                tree: parseTree(v['tree']),
              }),
          reason: v['reason'],
        }),
        'The draft was saved',
        (answer) => versionLines(answer),
      ),
    } : {}),
  }
}

/**
 * The decision tree as the backend will receive it.
 *
 * Parsed here only so it travels as JSON rather than as a string containing
 * JSON. Text that does not parse is sent unchanged, so the backend's validator
 * produces the refusal rather than this console inventing one — its message
 * names the branch that is wrong, and "invalid JSON" does not.
 */
function parseTree(raw: string | undefined): unknown {
  if (raw === undefined) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/**
 * Sending a draft for review — the author's last act on it.
 *
 * Separate from the publishing control because it carries the OTHER permission.
 * Support may submit and may not publish, and the two rendered as one picker
 * would need the wider of the two to enable the control at all.
 */
function submitVersionForm(f: ContentFamily, api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_CONTENT_WRITE, ROLE_AUTHOR, 'Sending a draft for review',
  )
  return {
    t: 'form',
    title: 'Send a draft for review',
    lead: 'Hands the draft to whoever holds the publishing permission. Nothing a customer reads changes here.',
    fields: [
      { name: 'key', label: 'Key', kind: 'text', required: true, mono: true },
      LANG_FIELD,
    ],
    reasons: AUTHOR_REASONS,
    reasonNote: 'Carried to the reviewer.',
    auditLine: `admin.help.${f.noun === 'article' ? 'article' : 'troubleshooter'}.submit · actor · ${f.subject}:<key>/<lang> · reason`,
    cta: 'Send for review',
    ctaNote: 'Nothing on the customer surface changes.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(transitionPath(f, v, 'submit'), { reason: v['reason'] }),
        'The draft was sent for review',
        (answer) => versionLines(answer),
      ),
    } : {}),
  }
}

/**
 * Publishing, rejecting or retiring — the three acts that change what a
 * customer reads.
 *
 * One control for three transitions is safe HERE and only here: all three carry
 * `help.article.publish`, so an action picker over them widens no permission.
 * The reason codes are filtered by the chosen action, so a retirement cannot be
 * recorded under "reviewed and correct" — read years later by somebody with
 * only the code, that difference is the whole audit trail.
 *
 * Retiring is the one that cannot be walked back into the same state, so it is
 * the one that asks for the key to be typed. The others are answered by
 * publishing again.
 */
function publishForm(f: ContentFamily, api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_CONTENT_PUBLISH, ROLE_PUBLISH, 'Publishing, rejecting or retiring a version',
  )
  return {
    t: 'form',
    title: 'Publish, reject or retire',
    lead: 'These three are what a customer sees. Publishing closes whatever version governed that language before it, and the answer names both — so publishing over somebody else\u2019s change a minute earlier is visible rather than silent.',
    fields: [
      { name: 'key', label: 'Key', kind: 'text', required: true, mono: true },
      LANG_FIELD,
      {
        name: 'action', label: 'Action', kind: 'select', required: true,
        options: [
          ['publish', 'publish — this version becomes what customers read'],
          ['reject', 'reject — send it back to the author'],
          ['retire', 'retire — the key stops serving this language'],
        ],
        note: 'Required, and there is no default. Retiring returns 410 Gone to anybody following a deep link.',
      },
    ],
    reasons: PUBLISH_REASONS,
    reasonNote: 'Filtered by the action, so a retirement is never recorded under a publication\u2019s reason.',
    auditLine: `admin.help.${f.noun === 'article' ? 'article' : 'troubleshooter'}.<action> · actor · ${f.subject}:<key>/<lang> · reason`,
    cta: 'Apply',
    ctaNote: 'Publishing takes effect on the customer surface immediately.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(transitionPath(f, v, String(v['action'])), { reason: v['reason'] }),
        `The version was ${String(v['action'])}${String(v['action']).endsWith('e') ? 'd' : 'ed'}`,
        (answer) => publishLines(answer),
      ),
    } : {}),
  }
}

/**
 * The route a transition posts to.
 *
 * The language is a PATH SEGMENT rather than a body field on all four of these,
 * and the backend explains why: it lets the audit subject be set before the
 * body is read, so a decode failure still leaves a row naming the article AND
 * the language rather than the article alone.
 */
function transitionPath(
  f: ContentFamily, v: Readonly<Record<string, string>>, action: string,
): string {
  return `${f.path}/${String(v['key'])}/versions/${String(v['lang'])}/${action}`
}

function versionLines(answer: unknown): readonly FormLine[] {
  if (!isObject(answer)) return []
  const lines: FormLine[] = []
  const version = answer['version']
  if (typeof version === 'number') lines.push(['version', String(version), ''])
  const state = text(answer['review_state'])
  if (state !== '') lines.push(['state', state, ''])
  const lang = text(answer['lang'])
  if (lang !== '') lines.push(['language', lang, ''])
  return lines
}

/**
 * A publish answers with TWO facts, and both are rendered.
 *
 * The version that now governs and the one it closed. An operator publishing
 * over a colleague's change from a minute earlier needs to see that a version
 * was closed — echoing only the request back would hide that the article had
 * moved under them.
 */
function publishLines(answer: unknown): readonly FormLine[] {
  if (!isObject(answer)) return []
  const lines: FormLine[] = [...versionLines(answer['version'])]
  const closed = answer['closed_version']
  if (typeof closed === 'number') {
    lines.push(['closed', String(closed), 'the version this replaced'])
  }
  return lines.length > 0 ? lines : versionLines(answer)
}

/**
 * Every control a content catalogue offers, in the order the workflow runs.
 *
 * Issue a key, author a draft, send it for review, then decide. The first three
 * are Support's and the last is Ops's, and rendering them in that order puts
 * the permission boundary where an operator will notice it: the first control
 * they cannot use is the one that changes what customers read.
 */
export function contentControls(f: ContentFamily) {
  return (api: ApiClient, caller: Caller): readonly Block[] => [
    createKeyForm(f, api, caller),
    authorVersionForm(f, api, caller),
    submitVersionForm(f, api, caller),
    publishForm(f, api, caller),
  ]
}

/*
 * The two loaders sit at the FOOT of the module, after the families and the
 * controls they name. `const` is not hoisted, so declaring them beside the
 * other reads would be a use-before-declaration — and the alternative, moving
 * two large data literals above the composition they belong with, would put
 * the field lists a hundred lines from the form that renders them.
 */

export const loadHelpArticles = loader(
  'Help centre', HELP_ARTICLES_PATH, ARTICLES_SOURCE, 'articles', 'article_key',
  contentControls(HELP_ARTICLE_FAMILY),
)

export const loadTroubleshooters = loader(
  'Troubleshooting trees', TROUBLESHOOTERS_PATH, TREES_SOURCE, 'troubleshooters', 'troubleshooter_key',
  contentControls(TROUBLESHOOTER_FAMILY),
)
